import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { SwissCard, StatusPillAuto, InlineSelect, EmptyState, KpiCard } from '../layout/SwissUI';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Car, RefreshCw, CheckCircle, XCircle, Clock, Mail, Phone, Calendar,
  Shield, FileText, User, MapPin, AlertTriangle, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { AttachmentList } from '../FileAttachments';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-900', border: 'border-amber-300', icon: Clock, label: 'Pending Review' },
  approved: { bg: 'bg-green-100', text: 'text-green-900', border: 'border-green-300', icon: CheckCircle, label: 'Approved' },
  declined: { bg: 'bg-red-100', text: 'text-red-900', border: 'border-red-300', icon: XCircle, label: 'Declined' },
  completed: { bg: 'bg-blue-100', text: 'text-blue-900', border: 'border-blue-300', icon: CheckCircle, label: 'Completed' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300', icon: XCircle, label: 'Cancelled' },
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const RentalRequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [allCounts, setAllCounts] = useState({ total: 0, pending: 0, approved: 0, declined: 0 });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [previewReq, setPreviewReq] = useState(null);
  const [adminNote, setAdminNote] = useState('');

  const loadCounts = async () => {
    try {
      const r = await fetch(`${API}/api/rentals/admin/requests`);
      const d = await r.json();
      const all = d.requests || [];
      setAllCounts({
        total: all.length,
        pending: all.filter(x => x.status === 'pending').length,
        approved: all.filter(x => x.status === 'approved').length,
        declined: all.filter(x => x.status === 'declined').length,
      });
    } catch {}
  };

  const load = async () => {
    setLoading(true);
    try {
      const url = filter === 'all' ? `${API}/api/rentals/admin/requests` : `${API}/api/rentals/admin/requests?status=${filter}`;
      const r = await fetch(url);
      const d = await r.json();
      setRequests(d.requests || []);
    } catch {
      toast.error('Failed to load rental requests');
    }
    setLoading(false);
    loadCounts();
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (requestId, status) => {
    try {
      const body = { status };
      if (adminNote) body.admin_notes = adminNote;
      const r = await fetch(`${API}/api/rentals/admin/requests/${requestId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || 'Update failed');
      }
      toast.success(status === 'approved' ? 'Request approved — renter notified via SMS' : status === 'declined' ? 'Request declined — renter notified via SMS' : `Status set to ${status}`);
      setPreviewReq(null);
      setAdminNote('');
      load();
    } catch (e) {
      toast.error(e.message || 'Update failed');
    }
  };

  const stats = allCounts;

  return (
    <SwissCard className="p-4 sm:p-6" testid="rental-requests-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Car className="h-5 w-5 text-[#E52B20]" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold">Self-drive</p>
            <h2 className="text-xl font-black tracking-tight">Rental Requests</h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 h-9 text-xs border-[#E5E5E5]" data-testid="rr-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending ({stats.pending})</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={load} className="h-9 w-9 border border-[#E5E5E5] hover:border-[#0A0A0A] bg-white flex items-center justify-center transition-colors" data-testid="rr-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* KPI strip — Swiss */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <KpiCard label="Total" value={stats.total} testid="rr-kpi-total" />
        <KpiCard label="Pending" value={stats.pending} tone="warn" testid="rr-kpi-pending" />
        <KpiCard label="Approved" value={stats.approved} tone="success" testid="rr-kpi-approved" />
        <KpiCard label="Declined" value={stats.declined} tone="danger" testid="rr-kpi-declined" />
      </div>

      {/* Request list */}
      {requests.length === 0 && !loading && (
        <EmptyState
          icon={Car}
          title={`No ${filter !== 'all' ? filter : ''} rental requests`}
          description="New rentals will appear here for triage."
          testid="rr-empty"
        />
      )}

      <div className="space-y-2">
        {requests.map(req => {
          return (
            <div key={req.request_id} className="border border-[#E5E5E5] p-3 hover:border-[#0A0A0A] hover:shadow-[4px_4px_0px_#0A0A0A] transition-all bg-white" data-testid={`rr-card-${req.request_id}`}>
              <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                <div className="flex items-start gap-2 min-w-0">
                  <InlineSelect
                    value={req.status || 'pending'}
                    options={[
                      { value: 'pending',   label: 'Pending' },
                      { value: 'approved',  label: 'Approved' },
                      { value: 'declined',  label: 'Declined' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'cancelled', label: 'Cancelled' },
                    ]}
                    onChange={(v) => updateStatus(req.request_id, v)}
                    renderTrigger={(val) => <StatusPillAuto status={val} />}
                    testid={`rr-status-edit-${req.request_id}`}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-bold truncate">{req.vehicle_name}</p>
                    <p className="text-[10px] text-[#666] font-mono">{req.request_id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => { setPreviewReq(req); setAdminNote(''); }} className="h-8 px-3 text-[11px] border border-[#E5E5E5] hover:border-[#0A0A0A] bg-white font-bold uppercase tracking-wider transition-colors flex items-center gap-1" data-testid={`rr-view-${req.request_id}`}>
                    <Eye className="h-3 w-3" /> View
                  </button>
                  {req.status === 'pending' && (
                    <>
                      <button onClick={() => updateStatus(req.request_id, 'approved')} className="h-8 px-3 text-[11px] bg-[#039855] hover:bg-[#02784a] text-white font-bold uppercase tracking-wider flex items-center gap-1" data-testid={`rr-approve-${req.request_id}`}>
                        <CheckCircle className="h-3 w-3" /> Approve
                      </button>
                      <button onClick={() => updateStatus(req.request_id, 'declined')} className="h-8 px-3 text-[11px] bg-white border border-[#E52B20] text-[#E52B20] hover:bg-[#E52B20] hover:text-white font-bold uppercase tracking-wider flex items-center gap-1 transition-colors" data-testid={`rr-decline-${req.request_id}`}>
                        <XCircle className="h-3 w-3" /> Decline
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="grid sm:grid-cols-4 gap-2 text-[11px] text-gray-700">
                <div className="flex items-center gap-1.5"><User className="h-3 w-3 text-gray-400" /><span className="truncate">{req.renterName}</span></div>
                <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-gray-400" /><span className="truncate">{req.renterPhone}</span></div>
                <div className="flex items-center gap-1.5"><Calendar className="h-3 w-3 text-gray-400" /><span>{fmtDate(req.pickupDate)} → {fmtDate(req.returnDate)}</span></div>
                <div className="flex items-center gap-1.5"><Clock className="h-3 w-3 text-gray-400" /><span>{fmtDate(req.created_at)}</span></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview Modal */}
      {previewReq && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPreviewReq(null)} data-testid="rr-preview-modal">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-black text-white px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] text-red-500 tracking-widest font-bold uppercase">Rental Request</p>
                <h3 className="font-bold">{previewReq.vehicle_name}</h3>
                <p className="text-[11px] text-gray-400 font-mono">{previewReq.request_id}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPreviewReq(null)} className="text-white hover:bg-white/10 h-8 w-8 p-0">✕</Button>
            </div>
            <div className="p-5 space-y-4">
              {/* Renter */}
              <section>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><User className="h-3 w-3" /> Renter</p>
                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500 text-[11px]">Name</span><p className="font-semibold">{previewReq.renterName}</p></div>
                  <div><span className="text-gray-500 text-[11px]">DOB</span><p className="font-semibold">{fmtDate(previewReq.dateOfBirth)}</p></div>
                  <div className="sm:col-span-2"><span className="text-gray-500 text-[11px]"><MapPin className="h-3 w-3 inline" /> Address</span><p className="font-semibold">{previewReq.renterAddress || '—'}</p></div>
                  <div><span className="text-gray-500 text-[11px]"><Phone className="h-3 w-3 inline" /> Phone</span><p className="font-semibold">{previewReq.renterPhone}</p></div>
                  <div><span className="text-gray-500 text-[11px]"><Mail className="h-3 w-3 inline" /> Email</span><p className="font-semibold">{previewReq.renterEmail}</p></div>
                  <div><span className="text-gray-500 text-[11px]">License</span><p className="font-semibold">{previewReq.licenseNumber} · {previewReq.licenseState}</p></div>
                </div>
              </section>

              {/* Dates */}
              <section>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Rental Period</p>
                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500 text-[11px]">Pickup</span><p className="font-semibold">{fmtDate(previewReq.pickupDate)} {previewReq.pickupTime}</p></div>
                  <div><span className="text-gray-500 text-[11px]">Return</span><p className="font-semibold">{fmtDate(previewReq.returnDate)} {previewReq.returnTime}</p></div>
                </div>
              </section>

              {/* Insurance */}
              <section>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Shield className="h-3 w-3" /> Insurance</p>
                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500 text-[11px]">Company</span><p className="font-semibold">{previewReq.insuranceCompany || '—'}</p></div>
                  <div><span className="text-gray-500 text-[11px]">Policy #</span><p className="font-semibold">{previewReq.insurancePolicy || '—'}</p></div>
                </div>
              </section>

              {/* Documentation */}
              <section>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><FileText className="h-3 w-3" /> Documentation Confirmed</p>
                <div className="flex flex-wrap gap-2 text-[11px]">
                  <span className={`px-2 py-1 rounded-full ${previewReq.hasLicense ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {previewReq.hasLicense ? '✓' : '✗'} Driver's License
                  </span>
                  <span className={`px-2 py-1 rounded-full ${previewReq.hasInsurance ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {previewReq.hasInsurance ? '✓' : '✗'} Insurance
                  </span>
                  <span className={`px-2 py-1 rounded-full ${previewReq.hasCreditCard ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {previewReq.hasCreditCard ? '✓' : '✗'} Credit Card
                  </span>
                  <span className={`px-2 py-1 rounded-full ${previewReq.acceptTerms ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {previewReq.acceptTerms ? '✓' : '✗'} Terms
                  </span>
                  <span className={`px-2 py-1 rounded-full ${previewReq.acceptLiability ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {previewReq.acceptLiability ? '✓' : '✗'} Liability
                  </span>
                </div>
              </section>

              {/* Uploaded Documents */}
              <section>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><FileText className="h-3 w-3" /> Uploaded Documents</p>
                <AttachmentList entityType="rental_request" entityId={previewReq.request_id} canDelete={false} emptyText="Renter did not upload documents — collect at pickup." />
              </section>

              {previewReq.additionalNotes && (
                <section>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Additional Notes from Renter</p>
                  <p className="text-sm bg-gray-50 border border-gray-200 rounded-md p-3">{previewReq.additionalNotes}</p>
                </section>
              )}

              {previewReq.admin_notes && (
                <section>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Previous Admin Notes</p>
                  <p className="text-sm bg-amber-50 border border-amber-200 rounded-md p-3">{previewReq.admin_notes}</p>
                </section>
              )}

              {previewReq.status === 'pending' && (
                <section className="border-t pt-4">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="h-3 w-3" /> Admin Notes (optional — saved with decision)
                  </p>
                  <Textarea
                    value={adminNote}
                    onChange={e => setAdminNote(e.target.value)}
                    placeholder="Internal note about this decision (not sent to renter)"
                    rows={2}
                    className="text-sm"
                    data-testid="rr-admin-note"
                  />
                  <div className="flex gap-2 mt-3">
                    <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => updateStatus(previewReq.request_id, 'approved')} data-testid="rr-modal-approve">
                      <CheckCircle className="h-4 w-4 mr-1.5" /> Approve & SMS Renter
                    </Button>
                    <Button variant="destructive" className="flex-1" onClick={() => updateStatus(previewReq.request_id, 'declined')} data-testid="rr-modal-decline">
                      <XCircle className="h-4 w-4 mr-1.5" /> Decline & SMS Renter
                    </Button>
                  </div>
                </section>
              )}

              {previewReq.status !== 'pending' && (
                <section className="border-t pt-4 flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => updateStatus(previewReq.request_id, 'completed')} data-testid="rr-modal-complete">
                    Mark Completed
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => updateStatus(previewReq.request_id, 'cancelled')} data-testid="rr-modal-cancel">
                    Cancel
                  </Button>
                </section>
              )}
            </div>
          </div>
        </div>
      )}
    </SwissCard>
  );
};

export default RentalRequestsTab;
